import { useCallback } from 'react';

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { parseApiError } from '@cast/api-client';
import { CAST_AI } from '@cast/constants';
import { notify, useDestructiveDialog } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import {
  getClusterQueryKey,
  getClustersQueryKey,
} from '@cast/shared-cluster-context-data-access';
import { Cluster } from '@cast/types';
import { isManagedByTerraform } from '@cast/utils';

type UseClusterDashboardDeleteOptions = {
  onDeleted?: () => void;
};

export const useDeleteClusterMutation = (
  cluster: Cluster,
  options?: UseClusterDashboardDeleteOptions
): UseMutationResult<void, Error, void> => {
  const queryClient = useQueryClient();
  const { id, managedBy } = cluster;
  const isTerraformManaged = isManagedByTerraform(managedBy);

  return useMutation({
    mutationFn: async () => {
      await apiClient.clusters.deleteCluster({ clusterId: id });
    },
    onSuccess: async () => {
      if (isTerraformManaged) {
        notify.success('Cluster removed');
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getClustersQueryKey({}),
        }),
        queryClient.invalidateQueries({
          queryKey: getClusterQueryKey({ id }),
        }),
      ]);

      options?.onDeleted?.();
    },
    onError: (error) => {
      notify.error(parseApiError(error));
    },
  });
};

export const useClusterDashboardDelete = (
  cluster: Cluster,
  options?: UseClusterDashboardDeleteOptions
) => {
  const { mutate, isPending } = useDeleteClusterMutation(cluster, options);
  const { openDialog } = useDestructiveDialog();
  const isTerraformManaged = isManagedByTerraform(cluster.managedBy);

  const deleteCluster = useCallback(() => {
    if (isTerraformManaged) {
      mutate();
      return;
    }

    openDialog({
      testId: 'cluster-dashboard-delete-dialog',
      title: `Remove ${cluster.name} from ${CAST_AI}?`,
      message: 'This action cannot be undone.',
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
      onConfirm: () => mutate(),
      slotProps: {
        cancelButton: { testId: 'cancel-cluster-delete' },
        confirmButton: { testId: 'confirm-cluster-delete' },
      },
    });
  }, [cluster.name, isTerraformManaged, mutate, openDialog]);

  return {
    deleteCluster,
    isDeleting: isPending,
  };
};
